import { useCallback, useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CardExpiryFieldConfig } from "@evergiving/common/dist/es2015/form";
import { useFieldValidationErrors } from "@/lib/hooks/useFieldValidationErrors";
import { RootState } from "@/lib/redux/types";
import { setFormValue, resetFormErrors } from "@/lib/redux/actions";
import { FieldError } from "@/lib/components/FieldError";
import { zeroPad, expandYear } from "@/lib/misc/dates";

export function CardExpiryField({ config }: { config: CardExpiryFieldConfig }) {
  const name = "cardExpiry";
  const valueFromStore = useSelector((state: RootState) => state[name].value);
  const [currentValue, setCurrentValue] = useState<string>(
    valueFromStore || ""
  );
  const fieldValidationErrors = useFieldValidationErrors(config);
  const anyErrors = useSelector((state: RootState) => state.errors[name]);
  const dispatch = useDispatch();
  const [isDirty, setIsDirty] = useState(false);
  const errorId = `${name}-errors`;
  const currentValueSplit = currentValue.split("-");

  const monthField = useRef<HTMLInputElement>(null);
  const yearField = useRef<HTMLInputElement>(null);

  const handleMonthChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { current: yearFieldEl } = yearField;
      const { value } = event.target;

      const newValue = `${currentValueSplit[0]}-${value.replace(/\D/g, "")}`;
      if (newValue !== currentValue) {
        setCurrentValue(newValue);
        dispatch(resetFormErrors(name));
        setIsDirty(true);
      }

      if (value.match(/(?:0[1-9]|1[0-2]|[1-9][.-/\\])/) && yearFieldEl) {
        yearFieldEl.focus();
        yearFieldEl.select();
      }
    },
    [currentValueSplit, currentValue, dispatch]
  );

  useEffect(() => {
    setCurrentValue(valueFromStore || "");
  }, [valueFromStore]);

  const handleYearChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const newValue = `${value.replace(/\D/g, "")}-${
        currentValueSplit[1] || ""
      }`;
      if (newValue !== currentValue) {
        setCurrentValue(newValue);
        dispatch(resetFormErrors(name));
        setIsDirty(true);
      }
    },
    [currentValue, currentValueSplit, dispatch]
  );

  const handleMonthPaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const { current: yearFieldEl } = yearField;
      const { current: monthFieldEl } = monthField;
      if (
        yearFieldEl &&
        monthFieldEl &&
        monthFieldEl.value.length === 0 &&
        yearFieldEl.value.length === 0
      ) {
        const pastedText = event.clipboardData.getData("text/plain");
        const match = pastedText.match(
          /^\s*(0?[1-9]|1[0-2])\s*[-./\\]\s*((?:20)?[0-9]{2})\s*$/
        );
        if (match) {
          // eslint-disable-next-line prefer-destructuring
          monthFieldEl.value = match[1];
          // eslint-disable-next-line prefer-destructuring
          yearFieldEl.value = match[2];
          yearFieldEl.focus();

          setIsDirty(true);

          event.preventDefault();
        }
      }
    },
    []
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { current: yearFieldEl } = yearField;
      const { current: monthFieldEl } = monthField;

      if (
        yearFieldEl &&
        monthFieldEl &&
        event.relatedTarget !== yearFieldEl &&
        event.relatedTarget !== monthFieldEl
      ) {
        const yearValue = yearFieldEl.value.replace(/\D/g, "");
        const monthValue = monthFieldEl.value.replace(/\D/g, "");
        const newValue =
          yearValue.length > 0 || monthValue.length > 0
            ? `${expandYear(yearValue, "near-future")}-${zeroPad(
                monthValue,
                2
              )}`
            : undefined;

        dispatch(
          setFormValue(
            name,
            newValue,
            fieldValidationErrors.forValue(newValue).length > 0
          )
        );
      }
    },
    [dispatch, fieldValidationErrors]
  );

  return (
    <>
      <div className="combinedField textField">
        <input
          name="ccmonth"
          value={currentValueSplit[1] || ""}
          placeholder={config.monthPlaceholderText}
          spellCheck="false"
          autoComplete="cc-exp-month"
          inputMode="numeric"
          size={2}
          maxLength={2}
          aria-required
          aria-invalid={(anyErrors && isDirty) || undefined}
          aria-describedby={anyErrors ? errorId : undefined}
          onChange={handleMonthChange}
          onPaste={handleMonthPaste}
          onBlur={handleBlur}
          ref={monthField}
        />
        <span className="fieldSeparator">/</span>
        <input
          name="ccyear"
          value={currentValueSplit[0] || ""}
          size={4}
          maxLength={4}
          placeholder={config.yearPlaceholderText}
          spellCheck="false"
          autoComplete="cc-exp-year"
          inputMode="numeric"
          aria-required
          onChange={handleYearChange}
          onBlur={handleBlur}
          ref={yearField}
        />
      </div>
      <div role="alert">
        {anyErrors && isDirty ? (
          <FieldError
            id={errorId}
            messages={fieldValidationErrors.forValue(valueFromStore)}
          />
        ) : null}
      </div>
    </>
  );
}
